import dynamic from "next/dynamic";
import Head from "next/head";
import Link from "next/link";
import { encryptInteger } from "@/Utils/encryptInteger";
import { useLoading } from "@/context/loading";
import MetaTags from "@/Components/SEO/MetaTags";
import { Schema } from '@/Components/SEO/Schema';
import withBaseUrl from "@/Utils/const";
import AnimatedReveal from "@/Components/HOC/AnimatedReveal";

const Hero = dynamic(() => import("@/Components/User/Home/Hero"), {
  loading: () => (
    <div className="h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#FA2CB4]"></div>
    </div>
  ),
  ssr: true,
});

const SupportingSlider = dynamic(
  () => import("@/Components/User/Home/SupportingSlider"),
  {
    loading: () => (
      <div className="h-40 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#FA2CB4]"></div>
      </div>
    ),
    ssr: false,
  }
);

const HomeShows = dynamic(() => import("@/Components/User/Home/HomeShows"), {
  loading: () => (
    <div className="h-96 flex items-center justify-center">
      <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#FA2CB4]"></div>
    </div>
  ),
  ssr: false,
});

const MobileSection = dynamic(
  () => import("@/Components/User/Home/MobileSection"),
  {
    loading: () => (
      <div className="h-64 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#FA2CB4]"></div>
      </div>
    ),
    ssr: true,
  }
);

const User = dynamic(() => import("@/Layouts/User"), {
  ssr: true,
});

export default function Home({ images, shows, homeData }) {
  const { isInitialLoad } = useLoading();

  const getBreadcrumbs = () => {
    return [
      {
        name: 'Home',
        url: withBaseUrl('/')
      }
    ];
  };

  return (
    <User>
      <Head>
        <title>Qoncert - Your Ultimate Concert Experience</title>
        <Schema breadcrumbs={getBreadcrumbs()} />
      </Head>
      <MetaTags 
        title="Qoncert - Your Ultimate Concert Experience"
        description="Find and book tickets for the best concerts and live music events. Qoncert offers a seamless experience for music lovers."
        ogImage="/assets/logo.webp"
        canonicalUrl="/"
      />
      <div className={`min-h-screen transition-opacity duration-500 ${isInitialLoad ? 'opacity-0' : 'opacity-100'}`}>
        <Hero />
        <div className="flex flex-col gap-[40px] lg:gap-[130px]">
          <SupportingSlider data={images.data} />
          <HomeShows data={shows?.data} />
          <div className="lg:pt-[100px] lg:pb-[80px] flex flex-col gap-[100px]">
            {homeData?.data?.map((mobile, i) => {
              const modifiedMobile = {
                ...mobile,
                ...(i % 2 === 0 && { order: "0" }),
                ...(i % 2 === 1 && { order: "2" }),
              };
              return <MobileSection key={mobile.id || i} data={modifiedMobile} />;
            })}
          </div>
          <div className="pb-4 lg:pb-0 lg:mb-[10px]">
            <div className="flex justify-center pb-[57px] lg:pb-[13px]">
              <AnimatedReveal animation="bounce-in" delay={isInitialLoad ? 2.1 : 0.3}>
                <div className="flex flex-row gap-6">
                  <a
                    href="https://play.qoncertapp.com/more-shows"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="py-2 w-[190.88px] min-w-max max-h-[54px] font-extrabold text-[20px] px-7 text-black bg-white rounded-2xl hover:bg-gray-100 transition-colors text-center"
                  >
                    Book a Show
                  </a>
                  <Link
                    href="/shows"
                    className="py-2 w-[190.88px] min-w-max max-h-[54px] font-extrabold text-[20px] px-7 text-white bg-transparent rounded-2xl border border-white/70 hover:border-white hover:bg-white/10 transition-colors text-center"
                  >
                    Get Tickets
                  </Link>
                </div>
              </AnimatedReveal>
            </div>
          </div>
        </div>
      </div>
    </User>
  );
}

export async function getStaticProps() {
  const BACKEND_URL = process.env.NEXT_PUBLIC_BACKEND_URL;

  try {
    const [imagesResponse, showsResponse, homeSSResponse] = await Promise.all([
      fetch(`${BACKEND_URL}/webapi/manageVenue`, {
        method: "POST",
        body: new URLSearchParams({ action: "getAllVenue" }),
      }),
      fetch(`${BACKEND_URL}/webapi/manageShow`, {
        method: "POST",
        body: new URLSearchParams({ 
          action: "getShow",
          by_period: "all" 
        }),
      }),
      fetch(`${BACKEND_URL}/webapi/manageHome`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      }),
    ]);

    const [images, shows, homeData] = await Promise.all([
      imagesResponse.ok ? imagesResponse.json() : { data: [] },
      showsResponse.ok ? showsResponse.json() : { data: [] },
      homeSSResponse.ok ? homeSSResponse.json() : { data: [] },
    ]);

    shows.data = shows.data.map((show) => ({
      ...show,
      encryptedId: encryptInteger(show.id),
    }));

    return {
      props: {
        images,
        shows,
        homeData,
      },
      revalidate: 180,
    };
  } catch (error) {
    console.error("Error fetching data:", error);
    return {
      props: {
        images: { data: [] },
        shows: { data: [] },
        homeData: { data: [] },
      },
      revalidate: 60,
    };
  }
}
